/**
 * Accessibility repairs for the Storeify store-locator widget.
 *
 * The widget is third-party markup injected after load, so it can't be fixed in Liquid. It
 * ships a search-type <select> and address/store-name inputs with no accessible name, and
 * on small screens its List/Map toggles are plain <div>s that a keyboard can't reach. This
 * element watches the widget as it renders and fills those gaps without changing how it
 * looks: names come from the section's settings or the widget's own visible text, and the
 * toggles become buttons that answer Enter/Space and report which view is showing. Its
 * "Store list" <h3> is re-levelled to 2 so the heading outline doesn't skip under the <h1>.
 */
class ScoopsStoreLocator extends HTMLElement {
  #observer = null;
  #queued = false;

  connectedCallback() {
    this.#repair();
    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#repair();
    });
  }

  #name(element, label) {
    if (!element || !label) return;
    if (element.labels?.length || element.hasAttribute('aria-labelledby')) return;
    if (element.getAttribute('aria-label') !== label) element.setAttribute('aria-label', label);
  }

  #repair() {
    this.#name(this.querySelector('select.chose-search-slt'), this.dataset.searchTypeLabel);

    for (const input of this.querySelectorAll('#address, #store_search')) {
      this.#name(input, input.getAttribute('placeholder') || this.dataset.searchLabel);
    }

    // The widget's "Store list" title is an <h3> directly under the page <h1>; expose it as
    // level 2 so the outline doesn't skip a level.
    for (const title of this.querySelectorAll('h3.title')) {
      if (title.getAttribute('aria-level') !== '2') title.setAttribute('aria-level', '2');
    }

    for (const toggle of this.querySelectorAll('.map-tab-mobile-bnt-item')) {
      if (toggle.getAttribute('role') !== 'button') toggle.setAttribute('role', 'button');
      if (toggle.tabIndex !== 0) toggle.tabIndex = 0;
      const pressed = String(toggle.classList.contains('active'));
      if (toggle.getAttribute('aria-pressed') !== pressed) toggle.setAttribute('aria-pressed', pressed);
    }
  }

  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const toggle = event.target.closest?.('.map-tab-mobile-bnt-item');
    if (!toggle || !this.contains(toggle)) return;
    event.preventDefault();
    toggle.click();
  };
}

if (!customElements.get('scoops-store-locator')) {
  customElements.define('scoops-store-locator', ScoopsStoreLocator);
}
